/**
 * GridCell Component - Individual note cell in the sequencer
 * Handles click, right-click, and touch interactions for toggling and editing notes
 */

import { memo, useState, useCallback } from 'react';
import { useMobile } from '../../hooks/useMobile';
import { moveNoteUp, moveNoteDown, moveNoteUpOctave, moveNoteDownOctave } from '../../utils/noteUtils';
import type { GridCellProps } from '../../types/grid';

/**
 * Get note color and hue based on pitch
 * Maps notes to a color spectrum with fine-grained hue variations
 */
function getNoteColorData(note: string | null): { color: string; hue: number } {
  if (!note) return { color: 'default', hue: 0 };
  
  // Extract note name and octave
  const match = note.match(/^([A-G]#?)(\d+)$/);
  if (!match) return { color: 'default', hue: 0 };
  
  const noteName = match[1];
  const octaveStr = match[2];
  
  if (!noteName || !octaveStr) return { color: 'default', hue: 0 };
  
  const octave = parseInt(octaveStr, 10);
  
  // Map note names to chromatic scale (0-11)
  const noteMap: Record<string, number> = {
    'C': 0, 'C#': 1, 'D': 2, 'D#': 3, 'E': 4, 'F': 5,
    'F#': 6, 'G': 7, 'G#': 8, 'A': 9, 'A#': 10, 'B': 11
  };
  
  const noteValue = noteMap[noteName] ?? 0;
  // Calculate MIDI note number (C4 = 60)
  const midiNote = (octave + 1) * 12 + noteValue;
  
  // Map MIDI note to hue with balanced variations
  // Group notes by octave ranges for distinct color zones
  const minMidi = 24; // C1
  
  // Calculate base hue from octave (major color shifts)
  const octaveRange = Math.floor((midiNote - minMidi) / 12); // 0-5 for 6 octaves
  const baseHue = octaveRange * 45; // 45° per octave (0, 45, 90, 135, 180, 225)
  
  // Add noticeable variation within octave based on note position
  const noteWithinOctave = noteValue / 12; // 0-1 within the octave
  const hueVariation = noteWithinOctave * 30; // 0° to +30° across the octave
  
  const hue = Math.round(baseHue + hueVariation);
  
  // Determine color category for base styling
  let color = 'default';
  if (midiNote < 36) color = 'red';
  else if (midiNote < 48) color = 'orange';
  else if (midiNote < 60) color = 'yellow';
  else if (midiNote < 72) color = 'green';
  else if (midiNote < 84) color = 'cyan';
  else color = 'purple';
  
  return { color, hue };
}

/**
 * GridCell component
 * Renders a single cell in the sequencer grid with support for:
 * - Click to toggle on/off
 * - Right-click to edit note
 * - Long-press (mobile) to edit note
 * - Double-tap (mobile) to edit note
 * - Connected cell styling for note sequences
 * 
 * @param props - Cell properties
 * @returns GridCell component
 */
function GridCellComponent({
  row,
  step,
  active,
  note,
  isCurrentStep,
  onToggle,
  onNoteEdit,
  onNoteChange,
}: GridCellProps) {
  const { isTouch, createLongPressHandlers, createDoubleTapHandlers } = useMobile();
  const [isHovered, setIsHovered] = useState(false);

  /**
   * Handle click event
   */
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    onToggle();
  };

  /**
   * Handle right-click (context menu) for note editing
   */
  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    // If cell is not active, activate it first
    if (!active) {
      onToggle();
    }
    // Always show context menu on right-click
    onNoteEdit(e);
  };

  /**
   * Handle keyboard shortcuts for note adjustment
   */
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    // Only handle keyboard shortcuts when cell is active and has a note
    if (!active || !note || !onNoteChange) return;

    let handled = false;

    if (e.key === 'w' || e.key === 'W') {
      e.preventDefault();
      if (e.shiftKey) {
        // Shift + W: Move up one octave
        onNoteChange(moveNoteUpOctave(note));
      } else {
        // W: Move up one semitone
        onNoteChange(moveNoteUp(note));
      }
      handled = true;
    } else if (e.key === 's' || e.key === 'S') {
      e.preventDefault();
      if (e.shiftKey) {
        // Shift + S: Move down one octave
        onNoteChange(moveNoteDownOctave(note));
      } else {
        // S: Move down one semitone
        onNoteChange(moveNoteDown(note));
      }
      handled = true;
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle();
      handled = true;
    }

    if (!handled && e.key !== 'Tab') {
      // Allow other keys to bubble up
    }
  }, [active, note, onNoteChange, onToggle]);

  /**
   * Handle mouse enter for hover state
   */
  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  /**
   * Handle mouse leave for hover state
   */
  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  /**
   * Build CSS classes for the cell
   */
  const cellClasses = [
    'cell',
    active && 'active',
    isCurrentStep && 'current-step',
  ]
    .filter(Boolean)
    .join(' ');

  /**
   * Create touch handlers for mobile
   */
  const longPressHandlers = isTouch ? createLongPressHandlers(onNoteEdit) : null;
  const doubleTapHandlers = isTouch ? createDoubleTapHandlers(onNoteEdit) : null;

  /**
   * Merge touch handlers (long-press takes precedence)
   */
  const touchHandlers =
    isTouch && longPressHandlers && doubleTapHandlers
      ? {
          onTouchStart: (e: React.TouchEvent) => {
            longPressHandlers.onTouchStart(e);
            doubleTapHandlers.onTouchStart(e);
          },
          onTouchMove: (e: React.TouchEvent) => {
            longPressHandlers.onTouchMove(e);
            doubleTapHandlers.onTouchMove(e);
          },
          onTouchEnd: (e: React.TouchEvent) => {
            longPressHandlers.onTouchEnd(e);
            doubleTapHandlers.onTouchEnd(e);
          },
          onTouchCancel: (e: React.TouchEvent) => {
            longPressHandlers.onTouchCancel(e);
            doubleTapHandlers.onTouchCancel(e);
          },
        }
      : {};

  // Get note color for styling
  const noteColorData = active ? getNoteColorData(note) : { color: 'default', hue: 0 };

  // Hover tooltip surfaces the otherwise-hidden pitch-edit affordances.
  const title = active
    ? isTouch
      ? 'Long-press to change pitch'
      : 'Right-click to change pitch • W/S to nudge (Shift = octave)'
    : isTouch
      ? 'Tap to add a note'
      : 'Click to add a note';

  return (
    <div
      className={cellClasses}
      data-row={row}
      data-step={step}
      data-note-color={noteColorData.color}
      data-hovered={isHovered}
      title={title}
      style={active && noteColorData.hue > 0 ? { '--note-hue': noteColorData.hue } as React.CSSProperties : undefined}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...touchHandlers}
      role="button"
      aria-label={`Cell row ${row + 1} step ${step + 1}${active ? ` active note ${note}` : ' inactive'}${isHovered && active ? '. Press W to move up, S to move down, Shift+W for octave up, Shift+S for octave down' : ''}`}
      aria-pressed={active}
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      {active && note ? note : ''}
    </div>
  );
}

/**
 * Memoized GridCell component
 * Only re-renders when props change
 */
export const GridCell = memo(GridCellComponent, (prevProps, nextProps) => {
  return (
    prevProps.active === nextProps.active &&
    prevProps.note === nextProps.note &&
    prevProps.isCurrentStep === nextProps.isCurrentStep
  );
});

GridCell.displayName = 'GridCell';
